<template>
  <v-app class="futuristic-app">
    <div class="mesh-background">
      <div class="circle c1"></div>
      <div class="circle c2"></div>
    </div>

    <v-app-bar app flat class="ultra-glass-header mt-4 mx-6 rounded-xl">
      <v-btn icon color="white" @click="$router.back()">
        <v-icon>mdi-chart-arc</v-icon>
      </v-btn>
      <v-toolbar-title
        class="white--text font-weight-black letter-spacing-2 text-uppercase"
      >
        KOMPOSISI KENDARAAN - {{ $route.query.name || "SEKTOR JALAN" }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <div class="d-none d-sm-flex flex-column align-end mr-4">
        <span class="gold--text caption font-weight-bold">BONDOWOSO BERKAH</span>
        <span class="white--text caption opacity-7">OPERASIONAL</span>
      </div>
      <v-badge dot color="#CFA22D" offset-x="10" offset-y="10">
        <v-icon color="white">mdi-database-sync</v-icon>
      </v-badge>
    </v-app-bar>

    <v-main>
      <v-container fluid class="pa-6 content-layer">
        <v-row justify="center" class="mb-8">
          <v-col cols="12" md="8" lg="6">
            <v-text-field
              v-model="searchQuery"
              label="Cari nama jalan"
              prepend-inner-icon="mdi-magnify"
              dark
              outlined
              rounded
              color="#CFA22D"
              class="cyber-search"
              hide-details
              clearable
            ></v-text-field>
            <div
              class="d-flex justify-space-between white--text text-caption mt-2 px-4 opacity-6 letter-spacing-1"
            >
              <span>Data: {{ listJalan.length }} SEKTOR</span>
              <span class="gold--text">Ditemukan: {{ filteredJalan.length }} HASIL</span>
            </div>
          </v-col>
        </v-row>

        <div v-if="loading" class="loading-overlay">
          <div class="cyber-spinner"></div>
          <p class="gold--text mt-4 letter-spacing-2">INITIALIZING DATA SCAN...</p>
        </div>

        <v-row v-else>
          <v-col cols="12" v-if="filteredJalan.length === 0" class="text-center mt-10">
            <v-icon size="64" color="rgba(255,255,255,0.1)">mdi-database-off</v-icon>
            <p class="white--text opacity-5 mt-4 letter-spacing-2">
              ERROR: SECTOR_NOT_FOUND
            </p>
          </v-col>

          <v-col
            v-for="(jalan, index) in filteredJalan"
            :key="jalan.id_jalan || index"
            cols="12"
            md="6"
            lg="4"
          >
            <v-card class="cyber-card overflow-hidden">
              <div class="card-corners">
                <div class="c-tl"></div>
                <div class="c-tr"></div>
                <div class="c-bl"></div>
                <div class="c-br"></div>
              </div>

              <v-card-title class="d-block pt-6">
                <div class="gold--text overline mb-1 opacity-7"></div>
                <div class="white--text text-h6 font-weight-black text-truncate">
                  {{ jalan.nama_jalan }}
                </div>
              </v-card-title>

              <v-card-text class="chart-wrapper">
                <div class="chart-container">
                  <Bar
                    v-if="renderChart"
                    :key="`chart-${index}-${chartKey}`"
                    :chart-data="getChartData(jalan.data_kendaraan)"
                    :options="chartOptions"
                  />
                  <div v-else class="no-data-msg">DATA_NOT_FOUND</div>
                </div>
              </v-card-text>

              <v-divider dark class="opacity-1"></v-divider>
              <div class="pa-3 d-flex justify-space-between align-center px-6">
                <span class="white--text text-caption opacity-6 font-weight-medium"
                  >Status</span
                >
                <v-icon small color="#018c74">mdi-shield-check-outline</v-icon>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import axios from "axios";
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

export default {
  components: { Bar },
  data() {
    return {
      searchQuery: "",
      listJalan: [],
      loading: true,
      renderChart: false,
      chartKey: 0,
      primaryColor: "#018c74",
      secondaryColor: "#CFA22D",
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "rgba(0, 30, 25, 0.9)",
            titleColor: "#CFA22D",
            bodyColor: "#fff",
            borderColor: "#018c74",
            borderWidth: 1,
            displayColors: false,
            padding: 12,
          },
        },
        scales: {
          y: {
            grid: { color: "rgba(255,255,255,0.05)" },
            ticks: { color: "rgba(255,255,255,0.4)", font: { family: "monospace" } },
          },
          x: {
            grid: { display: false },
            ticks: {
              color: "rgba(255,255,255,0.7)",
              font: { family: "monospace", size: 10 },
            },
          },
        },
      },
    };
  },
  computed: {
    filteredJalan() {
      if (!this.searchQuery) return this.listJalan;
      const query = this.searchQuery.toLowerCase();
      return this.listJalan.filter((jalan) =>
        jalan.nama_jalan.toLowerCase().includes(query)
      );
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    getChartData(dataKendaraan) {
      const items = dataKendaraan || [];
      return {
        labels: items.map((i) => i.jenis),
        datasets: [
          {
            label: "UNIT",
            backgroundColor: (context) => {
              const canvas = context.chart.ctx;
              const gradient = canvas.createLinearGradient(0, 0, 0, 220);
              gradient.addColorStop(0, this.primaryColor);
              gradient.addColorStop(1, "rgba(1, 140, 116, 0.05)");
              return gradient;
            },
            data: items.map((i) => i.jumlah),
            borderRadius: 4,
            borderWidth: 1,
            borderColor: this.primaryColor,
            hoverBackgroundColor: this.secondaryColor,
          },
        ],
      };
    },
    async fetchData() {
      try {
        this.loading = true;
        const res = await axios.get(
          "https://berkah.bondowosokab.go.id:5500/api/android/datajalan",
          {
            headers: {
              "Bondowoso-Berkah":
                "7039338ec2fa8ac649760ddd8404b711b207479377c846d41d5d24b7b21beb97",
            },
          }
        );
        const dataResult = res.data?.response?.data?.data?.jalan;
        if (dataResult) {
          this.listJalan = dataResult;
          this.renderChart = true;
          this.chartKey++;
        }
      } catch (error) {
        console.error("Transmission Error:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.mesh-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #001a16;
  z-index: 0;
  overflow: hidden;
}
.circle {
  position: absolute;
  filter: blur(120px);
  border-radius: 50%;
  opacity: 0.25;
}
.c1 {
  width: 600px;
  height: 600px;
  background: linear-gradient(90deg, #016b62 0%, #018c74 45%, #8bcdc6 100%);
  top: -15%;
  left: -10%;
  animation: move 25s infinite alternate;
}
.c2 {
  width: 500px;
  height: 500px;
  background: #cfa22d;
  bottom: -10%;
  right: -5%;
  animation: move 18s infinite alternate-reverse;
}

@keyframes move {
  from {
    transform: translate(0, 0);
  }
  to {
    transform: translate(100px, 50px);
  }
}

.ultra-glass-header {
  background: rgba(255, 255, 255, 0.03) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  z-index: 10;
}

.cyber-search ::v-deep .v-input__control {
  background: rgba(0, 30, 25, 0.5) !important;
  backdrop-filter: blur(10px);
}
.cyber-search ::v-deep fieldset {
  border: 1px solid rgba(1, 140, 116, 0.4) !important;
  transition: all 0.3s ease;
}
.cyber-search.v-input--is-focused ::v-deep fieldset {
  border: 1px solid #cfa22d !important;
  box-shadow: 0 0 15px rgba(207, 162, 45, 0.3);
}

.cyber-card {
  background: rgba(0, 30, 25, 0.6) !important;
  backdrop-filter: blur(15px);
  border: 1px solid rgba(1, 140, 116, 0.2) !important;
  border-radius: 24px !important;
  position: relative;
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
}
.cyber-card:hover {
  transform: translateY(-10px) scale(1.02);
  border-color: #cfa22d !important;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.6);
}

.card-corners div {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 2px solid #cfa22d;
  z-index: 2;
  pointer-events: none;
  opacity: 0.8;
}
.c-tl {
  top: 12px;
  left: 12px;
  border-right: 0;
  border-bottom: 0;
}
.c-tr {
  top: 12px;
  right: 12px;
  border-left: 0;
  border-bottom: 0;
}
.c-bl {
  bottom: 12px;
  left: 12px;
  border-right: 0;
  border-top: 0;
}
.c-br {
  bottom: 12px;
  right: 12px;
  border-left: 0;
  border-top: 0;
}

.chart-wrapper {
  height: 260px;
  padding: 10px 20px;
}
.chart-container {
  height: 100%;
  width: 100%;
  position: relative;
}
.no-data-msg {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #cfa22d;
  font-family: monospace;
  letter-spacing: 2px;
}

.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
}
.cyber-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba(207, 162, 45, 0.1);
  border-left-color: #cfa22d;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.gold--text {
  color: #cfa22d !important;
}
.letter-spacing-2 {
  letter-spacing: 2px;
}
.letter-spacing-1 {
  letter-spacing: 1px;
}
.opacity-7 {
  opacity: 0.7;
}
.opacity-6 {
  opacity: 0.6;
}
.opacity-5 {
  opacity: 0.5;
}
.opacity-1 {
  opacity: 0.1 !important;
}
</style>
